import { useEffect, useRef, useState } from "react";
import {
  AdyenCheckout,
  Dropin,
  GooglePay,
  ApplePay,
} from "@adyen/adyen-web";
import "@adyen/adyen-web/styles/adyen.css";
import "../../adyen-overrides.css";
import { v4 as uuidv4 } from "uuid";
import { initCardPayment, createCardPayment, finalizeCardPayment } from "@api";
import {
  isApplePayCancellation,
  getTranslations,
  resolveInitData,
  buildGooglePayExpressSection,
  buildApplePayExpressSection,
} from "../shared";
import {
  isInIframe,
  sendToParent,
  waitForParentResponse,
  sendResize,
} from "./postMessageBridge";

const AdyenPaymentWooCommerceExpress = ({ checkout }) => {
  const dropinRef = useRef(null);
  const paymentDataRef = useRef(null);
  const idempotencyKeyRef = useRef(uuidv4());
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentTotalRef = useRef(checkout.amountRequested);
  const shippingValidRef = useRef(false);

  const paymentParameters = {
    merchantId: checkout.merchant?.id,
    checkoutId: checkout.checkoutId || checkout.id,
    amount: checkout.amountRequested,
    amountRequested: checkout.amountRequested,
    reference: checkout.reference,
    customerName: checkout.customerName,
    source: "HostedCheckout",
    cardPaymentAllowed: true,
    amexPaymentAllowed: checkout.merchant?.paymentSettings?.allowAmex ?? checkout.amexPaymentAllowed ?? false,
  };

  useEffect(() => {
    document.documentElement.className = "";
  }, []);

  useEffect(() => {
    let mounted = true;

    async function init() {
      if (!mounted) return;
      setLoading(true);

      if (dropinRef.current) {
        dropinRef.current.unmount();
        dropinRef.current = null;
      }

      const initResponse = resolveInitData(
        await initCardPayment({
          merchantId: paymentParameters.merchantId,
          amount: paymentParameters.amount,
          cardAccountType: "Any",
          paymentProvider: "Adyen",
        }),
        "payment"
      );

      const { paymentMethods, clientKey, environment } = JSON.parse(initResponse.clientData);
      const adyenPaymentMethodsData = JSON.parse(paymentMethods);

      const paymentMethodsList = adyenPaymentMethodsData.paymentMethods || [];
      const availableTypes = paymentMethodsList.map((m) => m.type);
      const hasGooglePay = availableTypes.includes("googlepay") || availableTypes.includes("paywithgoogle");
      const hasApplePay = availableTypes.includes("applepay");
      const applePayConfig = paymentMethodsList.find((m) => m.type === "applepay")?.configuration;

      const adyenAmount = {
        value: Math.round((paymentParameters.amount || 0) * 100),
        currency: "GBP",
      };

      // --- Adyen callbacks ---
      const onSubmit = async (state, component, actions) => {
        if (isInIframe && !shippingValidRef.current) {
          console.error("[ontik-express] onSubmit rejected: no valid shipping address");
          actions.reject();
          return;
        }

        // Send the full authorized address to the WC parent.
        // Google Pay only provides a redacted address during INITIALIZE;
        // the full address is only available here after authorization.
        const { deliveryAddress, billingAddress } = state?.data ?? {};
        if (deliveryAddress || billingAddress) {
          sendToParent({ type: "ontik:express:authorized_address", deliveryAddress, billingAddress });
        }

        try {
          const payload = state?.data ? state.data : state;
          const response = await createCardPayment({
            ...paymentParameters,
            amount: currentTotalRef.current,
            paymentProvider: "Adyen",
            clientData: JSON.stringify(payload),
            idempotencyKey: idempotencyKeyRef.current,
          });
          paymentDataRef.current = response;

          if (response?.clientData) {
            const parsed = JSON.parse(response.clientData);
            actions.resolve({
              resultCode: parsed.resultCode,
              action: parsed.action,
              order: parsed.order,
              donationToken: parsed.donationToken,
            });
          } else {
            console.error("[ontik-express] onSubmit: no clientData in response", response);
            actions.reject();
          }
        } catch (err) {
          console.error("[ontik-express] onSubmit error:", err);
          actions.reject();
        }
      };

      const onAdditionalDetails = async (state, component, actions) => {
        try {
          const response = await finalizeCardPayment({
            merchantId: paymentParameters.merchantId,
            clientData: JSON.stringify(state.data),
            paymentProvider: "Adyen",
            paymentId: paymentDataRef.current?.id,
          });
          const result = JSON.parse(response.clientData);
          if (!result.resultCode) {
            console.error("[ontik-express] onAdditionalDetails: no resultCode");
            actions.reject();
            return;
          }
          actions.resolve({
            resultCode: result.resultCode,
            action: result.action,
            order: result.order,
            donationToken: result.donationToken,
          });
        } catch (err) {
          console.error("[ontik-express] onAdditionalDetails error:", err);
          actions.reject();
        }
      };

      const onPaymentCompleted = (result) => {
        const success = ["Authorised", "Pending", "Received"].includes(result.resultCode);
        sendToParent(success
          ? { type: "ontik:express:payment_success" }
          : { type: "ontik:express:payment_error", message: "Payment was declined." }
        );
      };

      const onPaymentFailed = (result) => {
        console.warn("[ontik-express] onPaymentFailed:", result);
        sendToParent({ type: "ontik:express:payment_error", message: "Payment failed. Please try again." });
        idempotencyKeyRef.current = uuidv4();
      };

      const onError = (error) => {
        console.error("[ontik-express] onError:", error);
        if (isApplePayCancellation(error) || error?.cause?.statusCode === "CANCELED") {
          setErrorMessage(null);
          init();
        } else {
          sendToParent({ type: "ontik:express:payment_error", message: "An error occurred during payment." });
        }
      };

      // --- Create AdyenCheckout + Drop-in ---
      const adyenCheckout = await AdyenCheckout({
        locale: "en-US",
        countryCode: "GB",
        environment: environment.toLowerCase(),
        showPayButton: true,
        clientKey,
        paymentMethodsResponse: adyenPaymentMethodsData,
        amount: adyenAmount,
        onSubmit,
        onAdditionalDetails,
        onPaymentCompleted,
        onPaymentFailed,
        onError,
        translations: getTranslations(),
      });

      const dropinContainer = document.getElementById("adyen-dropin-container");
      if (dropinContainer) {
        const instantPaymentTypes = [];
        if (hasGooglePay) instantPaymentTypes.push("googlepay");
        if (hasApplePay) instantPaymentTypes.push("applepay");

        const bridge = { isInIframe, sendToParent, waitForParentResponse };

        const dropin = new Dropin(adyenCheckout, {
          instantPaymentTypes,
          showPaymentMethods: false,
          paymentMethodComponents: [GooglePay, ApplePay],
          showRadioButton: false,
          disableFinalAnimation: true,
          paymentMethodsConfiguration: {
            googlepay: buildGooglePayExpressSection({ ...bridge, shippingValidRef, currentTotalRef }),
            applepay: buildApplePayExpressSection({ ...bridge, shippingValidRef, currentTotalRef, adyenAmount, clientKey, environment, applePayConfig }),
          },
        });
        dropin.mount(dropinContainer);
        dropinRef.current = dropin;
      }

      setLoading(false);
      setTimeout(sendResize, 100);

      const resizeObserver = new ResizeObserver(() => sendResize());
      const container = document.getElementById("adyen-express-container");
      if (container) resizeObserver.observe(container);
    }

    init();

    return () => {
      mounted = false;
      dropinRef.current?.unmount();
    };
  }, []);

  return (
    <>
      {errorMessage && (
        <div
          style={{ color: "#a00", fontSize: 13, padding: "8px 12px", marginBottom: 8, background: "#fff5f5", borderRadius: 4 }}
          role="alert"
        >
          {errorMessage}
        </div>
      )}
      {loading && (
        <div style={{ textAlign: "center", padding: 16, color: "#888", fontSize: 14 }}>
          Loading payment options...
        </div>
      )}
      <div id="adyen-express-container">
        <div id="adyen-dropin-container" />
      </div>
    </>
  );
};

export { AdyenPaymentWooCommerceExpress };
